import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Paper,
  PasswordInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconChartLine,
  IconDeviceDesktop,
  IconKey,
  IconPlugConnected,
  IconPlus,
  IconShoppingCart,
  IconTag,
} from "@tabler/icons-react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccess } from "@/hooks/useAccess";
import { lockProps } from "@/lib/permissions";
import { getBranches } from "@/api/branches";
import {
  createConnection,
  deleteConnection,
  getChannels,
  getConnections,
  getConnectionGroups,
  getRemoteProducts,
  updateConnection,
} from "@/api/connections";
import { notify } from "@/lib/notify";
import {
  ListCell,
  ListError,
  ListRow,
  ListScroll,
} from "@/components/DataList";
import { FormModalFooter } from "@/components/FormModalFooter";
import { PageTitle } from "@/components/PageTitle";
import { EMPTY_LIST } from "@/lib/queryClient";

const isSecretField = (field) => /password|secret|token|key/i.test(field);

const CHANNEL_TYPE_META = {
  pos: { label: "POS", color: "grape", Icon: IconDeviceDesktop },
  marketplace: {
    label: "Pazaryeri",
    color: "chocolate",
    Icon: IconShoppingCart,
  },
};

const channelTypeMeta = (type) =>
  CHANNEL_TYPE_META[type] ?? CHANNEL_TYPE_META.marketplace;

// Eşleşme rozeti. Dört durum ve dördü de ayrı: sorgulanıyor (iskelet), bağlantı ÇÖKTÜ (kırmızı),
// eksik eşleşme var (turuncu), hepsi eşleşti (yeşil).
//
// İskelet şart: sorgu POS'a/pazaryerine gidiyor ve saniyeler sürebiliyor. Rozetin yokluğu "hepsi
// eşleşti" değil "daha bilmiyoruz" demek, ama boş hücre bunu söylemiyordu.
//
// Çöken bağlantı ÖNCEDEN hiç rozet çizmiyordu — "yanlış sayı göstermektense hiçbir şey gösterme"
// gerekçesiyle. Ama boş hücre sağlıklı bir satırdan ayırt edilemiyordu: entegrasyonu kopmuş bir
// şube, listede sorunsuz görünüyordu. Sayı yerine ARIZANIN KENDİSİ yazılıyor artık.
function MatchBadge({ unmatched, pending, failed }) {
  if (pending) {
    return <Skeleton h={20} w={92} radius="xl" style={{ flexShrink: 0 }} />;
  }

  if (failed) {
    return (
      <Badge
        variant="light"
        color="red"
        size="sm"
        title="Kanalın ürün listesi alınamadı — kimlik bilgileri veya kanal servisi kontrol edilmeli"
        style={{ flexShrink: 0 }}
      >
        Bağlantı hatası
      </Badge>
    );
  }

  if (unmatched == null) {
    return null;
  }

  return unmatched > 0 ? (
    <Badge variant="light" color="orange" size="sm" style={{ flexShrink: 0 }}>
      {unmatched} eşleşmemiş
    </Badge>
  ) : (
    <Badge variant="light" color="teal" size="sm" style={{ flexShrink: 0 }}>
      Tümü eşleşti
    </Badge>
  );
}

// Vendor credential alanları için düzgün etiket + ipucu. Bilinmeyen alan otomatik
// başlığa düşer (kısaltmalar ID/API/URL düzeltilir).
const FIELD_META = {
  supplierId: { label: "Supplier ID", placeholder: "Trendyol satıcı numarası" },
  apiKey: { label: "API Key" },
  apiSecret: { label: "API Secret" },
  storeId: { label: "Store ID" },
  token: { label: "Token" },
  restaurantId: { label: "Restaurant ID" },
  vendorId: { label: "Vendor ID" },
  deviceToken: { label: "Device Token" },
  username: { label: "Kullanıcı Adı" },
  password: { label: "Şifre" },
  baseUrl: { label: "Base URL", placeholder: "https://..." },
};

const autoLabel = (field) =>
  field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase())
    .replace(/\b(Id|Api|Url)\b/g, (m) => m.toUpperCase());

const fieldLabel = (field) => FIELD_META[field]?.label ?? autoLabel(field);
const fieldPlaceholder = (field) => FIELD_META[field]?.placeholder ?? "";

function FormSection({
  icon: Icon,
  color = "chocolate",
  title,
  description,
  children,
}) {
  return (
    <Paper withBorder radius="md" p="md">
      <Group gap="xs" mb="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color={color} radius="md" size="md">
          <Icon size={16} />
        </ThemeIcon>
        <div>
          <Text fw={600} size="sm">
            {title}
          </Text>
          {description && (
            <Text size="xs" c="dimmed">
              {description}
            </Text>
          )}
        </div>
      </Group>
      <Stack gap="sm">{children}</Stack>
    </Paper>
  );
}

export function ConnectionFormModal({
  opened,
  onClose,
  connection,
  branches,
  channels,
  connections,
}) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(connection);
  const [branchId, setBranchId] = useState(
    connection ? String(connection.branchId) : "",
  );
  const [channelId, setChannelId] = useState(
    connection ? String(connection.channelId) : "",
  );
  const [credentials, setCredentials] = useState({});
  // Groups excluded from the price push. The names are the CHANNEL's own grouping, not our
  // menu categories — a POS organises its stock its own way.
  const [excludedGroups, setExcludedGroups] = useState(
    connection?.excludedGroups ?? [],
  );
  // Which price this connection pushes. Empty = follow the channel type's default below.
  const [priceBasis, setPriceBasis] = useState(connection?.priceBasis ?? "");
  const [trackRevenue, setTrackRevenue] = useState(
    connection?.trackRevenue ?? false,
  );

  // Modal ağaçta KALIYOR; kapatmak onu sökmüyor ve yukarıdaki başlangıç değerleri yalnızca ilk
  // bağlanışta okunuyor. Arka arkaya iki kez "yeni bağlantı" açıldığında ikinci pencere bir
  // öncekinin şubesi ve kanalıyla dolu geliyordu — kaydedilmiş bir bağlantı yeniden kaydedilecek
  // gibi görünüyor. Alanlar bu yüzden her açılışta tazeleniyor (bkz. CategoryFormModal, aynı kalıp).
  //
  // `connection` bağımlılıkta durabiliyor: dışarıda bir state ve yalnızca modal açılırken
  // yazılıyor, liste tazelendiğinde referansı değişmiyor — yani yazılmakta olan kimlik bilgisi
  // arada silinmiyor.
  useEffect(() => {
    if (!opened) {
      return;
    }

    setBranchId(connection ? String(connection.branchId) : "");
    setChannelId(connection ? String(connection.channelId) : "");
    setCredentials({});
    setExcludedGroups(connection?.excludedGroups ?? []);
    setPriceBasis(connection?.priceBasis ?? "");
    setTrackRevenue(connection?.trackRevenue ?? false);
  }, [opened, connection]);

  const channel =
    channels.find((item) => item.id === Number(channelId)) ?? null;
  const channelTypeDefault = channel?.type === "pos" ? "base" : "integration";
  const canTrackRevenue = channel?.supportsRevenue === true;
  // New connections default to the integration price (the partner switches to the customer/POS
  // price when they want it). Editing keeps the connection's current basis so saving never
  // silently flips a live POS off its customer price.
  const defaultPriceBasis = isEdit ? channelTypeDefault : "integration";
  const effectivePriceBasis = priceBasis || defaultPriceBasis;
  // So a price-basis-only edit can be saved (otherwise the submit button stays disabled unless
  // credentials are re-entered or a group filter is shown).
  const priceBasisChanged =
    isEdit &&
    effectivePriceBasis !== (connection?.priceBasis ?? channelTypeDefault);
  const trackRevenueChanged =
    isEdit &&
    canTrackRevenue &&
    trackRevenue !== (connection?.trackRevenue ?? false);
  // Bir şube aynı entegrasyona iki kez bağlanamaz — seçili şubede zaten bağlı kanalları listeden çıkar.
  const usedChannelIds = new Set(
    (connections ?? [])
      .filter(
        (item) =>
          item.branchId === Number(branchId) && item.id !== connection?.id,
      )
      .map((item) => item.channelId),
  );
  const branchName =
    branches.find((item) => item.id === Number(branchId))?.name ?? "";
  const typeMeta = channelTypeMeta(channel?.type);
  const fields = channel?.requiredCredentials ?? [];
  const credsComplete =
    fields.length > 0 && fields.every((field) => credentials[field]?.trim());
  const complete = branchId && channel && credsComplete;
  // Edit-only: the group list comes from the channel, so the connection must exist first.
  const showGroupFilter = isEdit && Boolean(channel?.supportsGroupFilter);

  const { data: groups = EMPTY_LIST, isLoading: groupsLoading } = useQuery({
    queryKey: ["connection-groups", connection?.id],
    queryFn: () => getConnectionGroups(connection.id),
    enabled: showGroupFilter,
  });

  const mutation = useMutation({
    mutationFn: () => {
      if (!isEdit) {
        return createConnection({
          branchId: Number(branchId),
          channelId: Number(channelId),
          credentials,
          priceBasis: effectivePriceBasis,
          trackRevenue: canTrackRevenue && trackRevenue,
        });
      }
      // Only send credentials when fully re-entered; the group filter can be saved alone.
      const payload = {};
      if (credsComplete) {
        payload.credentials = credentials;
      }
      if (showGroupFilter) {
        payload.excludedGroups = excludedGroups;
      }
      payload.priceBasis = effectivePriceBasis;
      if (canTrackRevenue) {
        payload.trackRevenue = trackRevenue;
      }
      return updateConnection(connection.id, payload);
    },
    onSuccess: () => {
      notify.success(isEdit ? "Bağlantı güncellendi" : "Bağlantı oluşturuldu");
      queryClient.invalidateQueries({ queryKey: ["connections"] });
      setCredentials({});
      onClose();
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "İşlem başarısız"),
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="lg"
      radius="md"
      title={
        <Text fw={600}>
          {isEdit ? "Bağlantı Bilgilerini Güncelle" : "Yeni Bağlantı"}
        </Text>
      }
    >
      <Stack gap="md">
        {isEdit && channel && (
          <Paper
            withBorder
            radius="md"
            p="sm"
            bg="var(--mantine-color-gray-0)"
            style={{ borderColor: "var(--mantine-color-gray-2)" }}
          >
            <Group gap="sm" wrap="nowrap">
              <ThemeIcon
                variant="light"
                color={typeMeta.color}
                radius="md"
                size="lg"
              >
                <typeMeta.Icon size={18} />
              </ThemeIcon>
              <div style={{ minWidth: 0 }}>
                <Text fw={600} size="sm" truncate>
                  {channel.name}
                </Text>
                <Text size="xs" c="dimmed" truncate>
                  {branchName}
                </Text>
              </div>
              <Badge variant="light" color={typeMeta.color} size="sm" ml="auto">
                {typeMeta.label}
              </Badge>
            </Group>
          </Paper>
        )}
        {!isEdit && (
          <FormSection
            icon={IconPlugConnected}
            title="Bağlantı"
            description="Hangi şube hangi entegrasyona bağlanacak"
          >
            <Select
              withCheckIcon={false}
              allowDeselect={false}
              label="Şube"
              placeholder="Şube seç"
              data={branches.map((branch) => ({
                value: String(branch.id),
                label: branch.name,
              }))}
              value={branchId}
              onChange={setBranchId}
            />
            <Select
              withCheckIcon={false}
              allowDeselect={false}
              label="Entegrasyon"
              placeholder="Entegrasyon seç"
              disabled={!branchId}
              data={channels
                .filter((item) => !usedChannelIds.has(item.id))
                .map((item) => ({ value: String(item.id), label: item.name }))}
              value={channelId}
              onChange={(value) => {
                setChannelId(value);
                setCredentials({});
                setPriceBasis("");
                setTrackRevenue(false);
              }}
            />
          </FormSection>
        )}
        {fields.length > 0 && (
          <FormSection
            icon={IconKey}
            title="Bağlantı Bilgileri"
            description={`${channel?.name} panelinden alınan erişim bilgileri`}
          >
            {fields.map((field) => {
              const Field = isSecretField(field) ? PasswordInput : TextInput;
              return (
                <Field
                  key={field}
                  label={fieldLabel(field)}
                  placeholder={fieldPlaceholder(field)}
                  value={credentials[field] ?? ""}
                  onChange={(event) => {
                    const { value } = event.currentTarget;
                    setCredentials((prev) => ({ ...prev, [field]: value }));
                  }}
                />
              );
            })}
          </FormSection>
        )}
        {isEdit && !fields.length && (
          <Text size="sm" c="dimmed">
            Bu entegrasyon için credential alanı tanımlı değil.
          </Text>
        )}
        {channel && (
          <FormSection
            icon={IconTag}
            title="Fiyat Gönderimi"
            description="Bu entegrasyona hangi fiyatın, hangi ürünler için gideceği"
          >
            <Select
              withCheckIcon={false}
              allowDeselect={false}
              label="Fiyat Kaynağı"
              data={[
                { value: "base", label: "Müşteri fiyatı (menü / şube)" },
                { value: "integration", label: "Entegrasyon fiyatı" },
              ]}
              value={effectivePriceBasis}
              onChange={(value) => value && setPriceBasis(value)}
            />
            {showGroupFilter && (
              <MultiSelect
                withCheckIcon={false}
                searchable
                clearable
                label="Hariç Tutulan Gruplar"
                description={`Seçilen grupların fiyatı gönderilmez. Gruplar ${channel.name} ürünlerinden gelir; boş bırakılırsa tümü gönderilir.`}
                disabled={groupsLoading}
                placeholder={groupsLoading ? "Gruplar yükleniyor…" : "Grup seç"}
                nothingFoundMessage="Grup bulunamadı"
                data={groups}
                value={excludedGroups}
                onChange={setExcludedGroups}
              />
            )}
          </FormSection>
        )}
        {canTrackRevenue && (
          <FormSection
            icon={IconChartLine}
            color="teal"
            title="Ciro Takibi"
            description="Siparişleri zaten POS adisyonuna düşen bir kanalda açma — aynı satış iki kez sayılır."
          >
            <Switch
              checked={trackRevenue}
              onChange={(event) => setTrackRevenue(event.currentTarget.checked)}
              color="teal"
              label="Cirosu şubenin toplamına eklensin"
            />
          </FormSection>
        )}
        <FormModalFooter>
          <Button variant="default" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            loading={mutation.isPending}
            disabled={
              isEdit
                ? !(
                    credsComplete ||
                    showGroupFilter ||
                    priceBasisChanged ||
                    trackRevenueChanged
                  )
                : !complete
            }
            onClick={() => mutation.mutate()}
          >
            {isEdit ? "Güncelle" : "Bağla"}
          </Button>
        </FormModalFooter>
      </Stack>
    </Modal>
  );
}

export default function Connections() {
  const { allBranches } = useAccess();
  // Bağlantı kurulumu (oluştur / credential düzenle / sil) tam yetki ister; eşleştirme ekranı
  // şube müdürüne açık — günlük iş orada.
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: branches = EMPTY_LIST } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranches,
  });
  const { data: channels = EMPTY_LIST } = useQuery({
    queryKey: ["channels"],
    queryFn: getChannels,
  });
  const {
    data: connections = EMPTY_LIST,
    isLoading,
    isError,
    refetch,
  } = useQuery({ queryKey: ["connections"], queryFn: getConnections });

  const [formOpened, { open: openForm, close: closeForm }] =
    useDisclosure(false);
  const [formConnection, setFormConnection] = useState(null);

  const branchName = useMemo(
    () => (id) => branches.find((branch) => branch.id === id)?.name ?? "-",
    [branches],
  );
  const channelById = useMemo(
    () => new Map(channels.map((item) => [item.id, item])),
    [channels],
  );

  // Per-connection eşleşmemiş entegrasyon ürünü sayısı. Cache'li remote-products (reload=false) →
  // POS'u tekrar yormaz; çöken bağlantı retry ile listeyi hammer'lamasın, rozet o satırda gizlenir.
  const remoteQueries = useQueries({
    queries: connections.map((connection) => ({
      queryKey: ["remote-products", connection.id],
      queryFn: () => getRemoteProducts(connection.id),
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  });
  // `pending` ve `unmatched: null` AYRI tutuluyor: ikisi de "sayı yok" ama biri geçici (sorgu
  // sürüyor), diğeri kalıcı (bağlantı çöktü). Tek bir null ikisini birleştirdiği için yükleme
  // sırasında hata durumunun sessizliği gösteriliyordu.
  const matchByConnection = useMemo(() => {
    const map = {};
    connections.forEach((connection, index) => {
      const query = remoteQueries[index];
      const data = query?.data;

      map[connection.id] = {
        pending: Boolean(query?.isPending),
        // isError AYRI taşınıyor: "unmatched == null" hem hatayı hem beklenmedik gövdeyi
        // birleştiriyordu ve rozet hangisi olduğunu bilemiyordu.
        failed: Boolean(query?.isError),
        unmatched: Array.isArray(data)
          ? data.filter((item) => !item.mappedProductId).length
          : null,
      };
    });
    return map;
  }, [connections, remoteQueries]);

  // Bağlantılar entegrasyona göre gruplanır: aynı kanalın şubeleri tek kart altında toplanır,
  // düz listede aynı kanal adının şube sayısı kadar tekrar etmesi yerine.
  const connectionGroups = useMemo(() => {
    const groups = new Map();

    connections.forEach((connection) => {
      let group = groups.get(connection.channelId);

      if (!group) {
        group = {
          channelId: connection.channelId,
          channel: channelById.get(connection.channelId),
          items: [],
        };
        groups.set(connection.channelId, group);
      }

      group.items.push(connection);
    });

    const collate = (a, b) => a.localeCompare(b, "tr");
    const rank = (group) => (group.channel?.type === "pos" ? 0 : 1);

    return [...groups.values()]
      .map((group) => ({
        ...group,
        items: [...group.items].sort((a, b) =>
          collate(branchName(a.branchId), branchName(b.branchId)),
        ),
      }))
      .sort(
        (a, b) =>
          rank(a) - rank(b) ||
          collate(a.channel?.name ?? "", b.channel?.name ?? ""),
      );
  }, [connections, channelById, branchName]);

  // Kart başlığındaki toplam. Şubelerden BİRİ bile hâlâ yükleniyorsa toplam eksik olurdu ve
  // kullanıcı onu nihai sanardı — hepsi gelene kadar iskelet. Hiçbirinin sayısı bilinmiyorsa
  // (tamamı hata) rozet gizlenir (null).
  const groupMatch = (group) => {
    const rows = group.items.map(
      (connection) => matchByConnection[connection.id],
    );

    if (rows.some((row) => row?.pending)) {
      return { pending: true };
    }

    return {
      unmatched: rows.reduce(
        (total, row) =>
          row?.unmatched == null ? total : (total ?? 0) + row.unmatched,
        null,
      ),
    };
  };

  const deleteMutation = useMutation({
    mutationFn: deleteConnection,
    onSuccess: () => {
      notify.success("Bağlantı silindi");
      queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
    onError: () => notify.error("Bağlantı silinemedi"),
  });

  const handleDelete = (connection) =>
    modals.openConfirmModal({
      title: "Bağlantıyı sil",
      centered: true,
      children: (
        <Text size="sm">
          {channelById.get(connection.channelId)?.name} bağlantısını ve tüm
          eşleştirmelerini silmek istediğine emin misin?
        </Text>
      ),
      labels: { confirm: "Sil", cancel: "Vazgeç" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(connection.id),
    });

  return (
    <Stack gap="lg">
      <PageTitle>Entegrasyonlar</PageTitle>
      <Group justify="space-between">
        <Group>
          <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
            <IconPlugConnected size={26} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Title order={2}>Entegrasyonlar</Title>
            <Text c="dimmed" size="sm">
              Pazaryeri ve POS bağlantılarını yönet, ürünleri eşleştir
            </Text>
          </div>
        </Group>
        {/* Bağlantı kurmak credential girmek demek — API'de requireFullAccess. Yetkisiz
                    kullanıcıda buton gizlenmiyor, kilitleniyor. */}
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => {
            setFormConnection(null);
            openForm();
          }}
          {...lockProps(allBranches, { disabled: !branches.length })}
        >
          Yeni Bağlantı
        </Button>
      </Group>

      {isError ? (
        <ListError title="Bağlantılar alınamadı" onRetry={refetch} />
      ) : isLoading ? (
        <Card withBorder radius="md" p="md">
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        </Card>
      ) : connections.length === 0 ? (
        <Card withBorder radius="md" p="md">
          <Text c="dimmed" ta="center" py="xl">
            Henüz bağlantı yok.{" "}
            {branches.length ? "" : "Önce bir şube ekleyin."}
          </Text>
        </Card>
      ) : (
        // Geniş ekranda ikili grid, dar ekranda alt alta.
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {connectionGroups.map((group) => {
            const meta = channelTypeMeta(group.channel?.type);

            return (
              <Card key={group.channelId} withBorder radius="md" p={0}>
                <Group
                  justify="space-between"
                  wrap="nowrap"
                  gap="md"
                  px="md"
                  py="sm"
                  bg="var(--mantine-color-gray-0)"
                  style={{
                    borderBottom: "1px solid var(--mantine-color-gray-2)",
                  }}
                >
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    <ThemeIcon
                      size={38}
                      radius="md"
                      variant="light"
                      color={meta.color}
                    >
                      <meta.Icon size={20} stroke={1.6} />
                    </ThemeIcon>
                    <div style={{ minWidth: 0 }}>
                      <Text fw={600} truncate>
                        {group.channel?.name ?? "Bilinmeyen entegrasyon"}
                      </Text>
                      <Group gap={6} wrap="nowrap">
                        <Text size="xs" c="dimmed">
                          {meta.label} · {group.items.length} şube
                        </Text>
                        {group.channel?.supportsRevenue && (
                          <Badge
                            variant="light"
                            color="chocolate"
                            size="xs"
                            title="Bu entegrasyon ciro raporlayabilir"
                          >
                            Ciro
                          </Badge>
                        )}
                      </Group>
                    </div>
                  </Group>
                  <MatchBadge {...groupMatch(group)} />
                </Group>

                <ListScroll minWidth={500}>
                  {group.items.map((connection, index) => (
                    <div key={connection.id}>
                      {index > 0 && <Divider color="gray.1" />}
                      <ListRow bd="none">
                        <ListCell flex ta="left">
                          <Text size="sm" fw={500} truncate>
                            {branchName(connection.branchId)}
                          </Text>
                        </ListCell>
                        <ListCell w={120}>
                          <MatchBadge {...matchByConnection[connection.id]} />
                        </ListCell>
                        <ListCell w={215} ta="right">
                          <Group gap="xs" justify="flex-end" wrap="nowrap">
                            <Button
                              size="compact-sm"
                              variant="light"
                              onClick={() =>
                                navigate(
                                  `/connections/${connection.id}/mappings`,
                                )
                              }
                            >
                              Eşleştirme
                            </Button>
                            {/* Eşleştirme ekranı prices:sync ile de
                                                            açılabilir (fiyat göndermek için eşleşme
                                                            gerekir); credential ("Bilgiler") ve silme
                                                            API'de requireFullAccess — burada gizlenmiyor,
                                                            kilitleniyor. */}
                            <Button
                              size="compact-sm"
                              variant="default"
                              {...lockProps(allBranches)}
                              onClick={() => {
                                setFormConnection(connection);
                                openForm();
                              }}
                            >
                              Bilgiler
                            </Button>
                            {/* Silme, bağlantının tüm ürün
                                                            eşleştirmelerini de götürür. */}
                            <Button
                              size="compact-sm"
                              variant="subtle"
                              color="red"
                              {...lockProps(allBranches)}
                              onClick={() => handleDelete(connection)}
                            >
                              Sil
                            </Button>
                          </Group>
                        </ListCell>
                      </ListRow>
                    </div>
                  ))}
                </ListScroll>
              </Card>
            );
          })}
        </SimpleGrid>
      )}

      <ConnectionFormModal
        opened={formOpened}
        onClose={closeForm}
        connection={formConnection}
        branches={branches}
        channels={
          formConnection
            ? channels.filter((item) => item.id === formConnection.channelId)
            : channels
        }
        connections={connections}
      />
    </Stack>
  );
}
